import React from 'react';
import { Field, Label, Select, ValidationMessage } from '@innovationnorway/cl-react';
import { FormGroupErrorContext } from './FormGroup';

export type SelectOption = {
  value: number | string | readonly string[];
  text: React.ReactNode | string;
};

export type SelectWrapperOptionGroup = {
  label: string;
  options: Array<SelectOption>;
};

export interface SelectWrapperProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  className?: string;
  error?: React.ReactNode | boolean;
  id?: string;
  inputClassName?: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  name?: string;
  options: Array<SelectOption | SelectWrapperOptionGroup>;
}

export const SelectWrapper = React.forwardRef<HTMLSelectElement, SelectWrapperProps>(
  (
    { className, error, id, inputClassName, label, description, name, options, ...props }: SelectWrapperProps,
    ref,
  ) => {
    const context = React.useContext(FormGroupErrorContext);
    const resolvedError = context.error || error;

    return (
      <Field className={className}>
        {label && (
          <Label>
            {label}
            {props.required && ' *'}
          </Label>
        )}

        {description && <Field.Description data-size="sm">{description}</Field.Description>}

        <Select
          ref={ref}
          id={id}
          name={name}
          className={inputClassName}
          aria-invalid={!!resolvedError}
          {...props}
        >
          {options.map((option, index) => (
            <Option key={index} option={option} />
          ))}
        </Select>

        {!context.error && !!error && typeof error !== 'boolean' && (
          <ValidationMessage>{error}</ValidationMessage>
        )}
      </Field>
    );
  },
);

SelectWrapper.displayName = 'SelectWrapper';

interface OptionProps {
  option: SelectWrapperOptionGroup | SelectOption;
}

const Option: React.FC<OptionProps> = ({ option }) => {
  const optionIsGroup = (
    option: SelectOption | SelectWrapperOptionGroup,
  ): option is SelectWrapperOptionGroup => {
    return (option as SelectWrapperOptionGroup).options !== undefined;
  };

  if (optionIsGroup(option)) {
    return (
      <Select.Optgroup label={option.label}>
        {option.options.map((option, index) => (
          <Select.Option value={option.value} key={index}>
            {option.text}
          </Select.Option>
        ))}
      </Select.Optgroup>
    );
  }

  return <Select.Option value={option.value}>{option.text}</Select.Option>;
};
